import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ProviderConfigurationService } from '@philips-emr/hpms-provider-commons';
import { Link, SocialNetworksAPI, TextAPI } from '@philips-emr/tws-components';
import { DismissStyle, ThemeTonalRange, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnCatch, OnMount } from '@philips-emr/tws-core/lib/client';
import { PromiseEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService, ThemeService, ToastService as ToastServiceFw } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';
import { LoginAPI } from '@philips-emr/tws-foundation';
import { getBreakpoint } from '@philips-emr/tws-utils';

import { LoginAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

interface User {
  nrSeqWSuiteUser: number,
}

interface LayoutConfiguration {
  nrContactPhone: string,
  dsFooterEmail: string,
  dsAddress: string,
}

@Controller({ fragment: FragmentNames.LOGIN })
export default class LoginController extends BaseController {

  @Inject(Aliases.LOGIN_COMPONENT)
  private loginAPI: LoginAPI;

  @Inject(Aliases.TEXT_INFO)
  private textInfoAPI: TextAPI;

  @Inject(Aliases.TEXT_EMAIL)
  private textEmail: TextAPI;

  @Inject(Aliases.TEXT_TELEPHONE)
  private textTelephone: TextAPI;

  @Inject(Aliases.SOCIAL_NETWORKS)
  private socialNetworksAPI: SocialNetworksAPI;

  @OnMount(Aliases.LOGIN_COMPONENT)
  onMountlogin(): void {
    void ProviderConfigurationService.getConfigurationData().then(data => {
      const { dsAddress, nrContactPhone, dsFooterEmail } = data as unknown as LayoutConfiguration;
      const telephone = `${MessageService.translate('provider.telephone').concat(':')}`;
      const email = `${MessageService.translate('provider.email').concat(':')}`;
      this.textInfoAPI.setValue(dsAddress);
      this.textTelephone.setValue(nrContactPhone ? `${telephone} ${nrContactPhone}` : '');
      this.textEmail.setValue(dsFooterEmail ? `${email} ${dsFooterEmail}` : '');

      data.dsLinkFacebook && this.socialNetworksAPI.setFacebook(data.dsLinkFacebook as string);
      data.dsLinkLinkedin && this.socialNetworksAPI.setLinkedin(data.dsLinkLinkedin as string);
      data.dsLinkYoutube && this.socialNetworksAPI.setYoutube(data.dsLinkYoutube as string);
      data.dsLinkInstagram && this.socialNetworksAPI.setInstagram(data.dsLinkInstagram as string);
      data.dsLinkTwitter && this.socialNetworksAPI.setTwitter(data.dsLinkTwitter as string);
      this.loginAPI.setLogo(data.dsUrlLogoHeaderLogin as string);
    });

    ThemeService.setContentTonalRange(ProviderConfigurationService.getLoginTonalRange());
    setTimeout(() => this.showCookiesWarning(), 1000);
  }

  @OnCatch(Aliases.LOGIN_COMPONENT)
  onCatch(event: PromiseEvent<Record<string, unknown>>): void {
    const response = event.response.response as Response;
    void response.json().then((content) => {
      const pwExpired = '870103';
      if (content.entity == pwExpired) {
        this.passwordExpired();
      } else {
        this.showNotification(content);
      }
    }
    );
  }

  showNotification(content: any): void {
    this.loginAPI.setNotification({
      message: content.entity,
      level: ToastLevel.WARNING,
      highlight: true,
    });
  }

  private getMessageCookiePolicies(): ReactElement {
    return (
      <Link onClick={() => this.showCookiesModal()} value={MessageService.translate('provider.cookies-policy')} />
    );
  }

  showCookiesModal(): void {
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: 'cookie-policies',
      title: 'provider.cookies-policy',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    };
    const breakpoints = getBreakpoint() as string;
    if (breakpoints == 'sm' || breakpoints == 'xsm') {
      ModalService.openFullscreen(modalWithFragment);
    } else {
      ModalService.openDialog(modalWithFragment);
    }
  }

  private showCookiesWarning(): void {
    ToastServiceFw.open({
      id: 'c-toast-notification__message--cookies',
      title: MessageService.translate('provider.cookies-policy'),
      message: MessageService.translate('provider.message-cookie-policies'),
      dismissible: true,
      dismissStyle: DismissStyle.BUTTON,
      showIcon: true,
      dismissExpression: MessageService.translate('provider.ok'),
      action: this.getMessageCookiePolicies(),
      level: ToastLevel.WARNING,
      highlight: true,
    });
  }

  passwordExpired(): void {
    const { username } = this.loginAPI.state;
    void HttpService.get<User>(Endpoint.GET.LOGIN_USER, { username }).then((response) => {
      if (response.body) {
        const { nrSeqWSuiteUser } = response.body;
        this.showRenewPasswordModal(nrSeqWSuiteUser);
      }
    });
  }  

  showRenewPasswordModal(nrSeqWSuiteUser: number): void {
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: FragmentNames.CHANGE_PW,
      contextData: { nrSeqWSuiteUser },
      id: 'renew-password',
    };
    const breakpoints = getBreakpoint() as string;
    if (breakpoints == 'sm' || breakpoints == 'xsm') {
      ModalService.openFullscreen(modalWithFragment);
    } else {
      ModalService.openDialog(modalWithFragment);
    }
  }
}
